import React from 'react';
import SEOHead from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <>
      <SEOHead
        title="Datenschutzerklärung"
        description="Datenschutzerklärung der EU AI Act Training Platform. Erfahren Sie, wie wir Ihre personenbezogenen Daten verarbeiten und schützen."
        keywords={['Datenschutz', 'DSGVO', 'Privacy Policy', 'Datenschutzerklärung']}
        canonicalUrl="/datenschutz"
        noIndex={true}
      />
      <BreadcrumbSchema items={[
        { name: 'Startseite', url: '/' },
        { name: 'Datenschutz', url: '/datenschutz' }
      ]} />
      <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Datenschutzerklärung</h1>
          
          <div className="prose max-w-none text-gray-600">
            <p className="mb-8">
              <strong>KI Navigatoren GmbH</strong><br />
              Mittelhofer Weg 24<br />
              73466 Lauchheim<br />
              Handelsregister: HRB 750033, Handelsregister B des Amtsgerichts Ulm<br />
              E-Mail: aiko@ki-navigatoren.de
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">1. Verantwortlicher</h2>
            <p>
              Verantwortlich für die Datenverarbeitung auf dieser Website ist<br />
              KI Navigatoren GmbH,<br />
              vertreten durch die Geschäftsführer Robert Weber und Marko Bauer.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">2. Allgemeines zur Datenverarbeitung</h2>
            <p className="mb-4">
              Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Personenbezogene Daten (z. B. Name, E-Mail-Adresse, Firma, Adresse, Zahlungsdaten) werden vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften verarbeitet.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Hosting & Speicherung</h3>
            <ul className="list-disc pl-5 mb-6">
              <li><strong>Webhosting:</strong> Die Website liegt bei Netlify.</li>
              <li><strong>Datenbank & Speicher:</strong> Alle Kundendaten (Registrierungen, Kurszugänge, Testergebnisse) werden in <strong>Supabase</strong> in einem Frankfurter Rechenzentrum gespeichert.</li>
              <li><strong>Vimeo:</strong> Öffentliche Videos binden wir über Vimeo-Link-Einbettung ein. Dabei werden beim Abspielen Nutzungsdaten (z. B. IP-Adresse) an Vimeo übermittelt.</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">3. Erhobene Daten und Verwendungszwecke</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-2 px-4 text-left">Datenkategorie</th>
                    <th className="py-2 px-4 text-left">Zweck</th>
                    <th className="py-2 px-4 text-left">Rechtsgrundlage</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="py-2 px-4">Name, Vorname, Firma, Adresse</td>
                    <td className="py-2 px-4">Vertragsschluss, Zertifikats­ausstellung</td>
                    <td className="py-2 px-4">Art. 6 Abs. 1 lit. b DSGVO</td>
                  </tr>
                  <tr className="border-t">
                    <td className="py-2 px-4">E-Mail-Adresse</td>
                    <td className="py-2 px-4">Kommunikation, Versand von Zertifikaten</td>
                    <td className="py-2 px-4">Art. 6 Abs. 1 lit. b DSGVO</td>
                  </tr>
                  <tr className="border-t">
                    <td className="py-2 px-4">Zahlungsdaten (Kreditkarte, SEPA-Mandat)</td>
                    <td className="py-2 px-4">Abwicklung von Zahlungen über Stripe</td>
                    <td className="py-2 px-4">Art. 6 Abs. 1 lit. b DSGVO</td>
                  </tr>
                  <tr className="border-t">
                    <td className="py-2 px-4">Nutzungsdaten (Login-Zeit, Testergebnis)</td>
                    <td className="py-2 px-4">Betrieb, Abrechnung, Statistik</td>
                    <td className="py-2 px-4">Art. 6 Abs. 1 lit. f DSGVO</td>
                  </tr>
                  <tr className="border-t">
                    <td className="py-2 px-4">Cookies & Tracking</td>
                    <td className="py-2 px-4">Funktionalität der Seite, Analytics</td>
                    <td className="py-2 px-4">Art. 6 Abs. 1 lit. f DSGVO</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">4. Cookies & Tools</h2>
            <ul className="list-disc pl-5 mb-6">
              <li><strong>Essenzielle Cookies</strong> (z. B. Session-ID) sind notwendig, damit die Seite funktioniert.</li>
              <li><strong>Google Tag Manager (GTM):</strong> Wir nutzen den Google Tag Manager (Container-ID: GTM-TL4VNH3T), um verschiedene Tracking- und Marketing-Tools zentral zu verwalten. Der GTM selbst erfasst keine personenbezogenen Daten, sondern dient als technisches Verwaltungstool.</li>
              <li><strong>Google Analytics 4:</strong> Über den Google Tag Manager wird Google Analytics 4 (Tracking-ID: G-H17KEHWJ4T) eingebunden. Dies erfolgt nur nach Ihrer Einwilligung. Google Analytics erfasst anonymisierte Nutzungsdaten zur Verbesserung unserer Website. Die Daten werden auf Servern in den USA verarbeitet. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).</li>
              <li><strong>Google Ads:</strong> Sofern Sie eingewilligt haben, nutzen wir Google Ads für Marketing-Zwecke über den Google Tag Manager. Dabei können Conversion-Tracking und Remarketing-Funktionen zum Einsatz kommen.</li>
              <li><strong>Vimeo Player:</strong> Lädt beim Video-Start Skripte von Vimeo und setzt ggf. Cookies auf Ihrem Endgerät.</li>
              <li><strong>Cookie-Einwilligung:</strong> Sie können Ihre Einwilligung jederzeit widerrufen, indem Sie Ihren Browser-Cache leeren oder uns kontaktieren.</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">5. Stripe-Integration</h2>
            <p className="mb-6">
              Zahlungen wickeln wir über <strong>Stripe</strong> ab. Dabei überträgt Stripe bei der Zahlungsabwicklung Ihre Zahlungsdaten sicher an seine Server. Wir selbst speichern nur den Zahlungsstatus und die für Rechnungslegung notwendigen Metadaten.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">6. Weitergabe an Dritte</h2>
            <p className="mb-6">
              Ihre Daten geben wir nur weiter, wenn dies zur Vertrags­durchführung erforderlich ist (z. B. an Stripe, Supabase oder E-Mail-Dienstleister) oder wir gesetzlich dazu verpflichtet sind.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">7. Betroffenenrechte</h2>
            <p className="mb-2">Sie haben das Recht auf</p>
            <ul className="list-disc pl-5 mb-4">
              <li>Auskunft über Ihre gespeicherten Daten,</li>
              <li>Berichtigung, Löschung oder Einschränkung der Verarbeitung,</li>
              <li>Datenübertragbarkeit,</li>
              <li>Widerruf erteilter Einwilligungen,</li>
              <li>Beschwerde bei einer Aufsichtsbehörde.</li>
            </ul>
            <p className="mb-6">Bitte richten Sie Ihre Anfragen an info@ki-navigatoren.de.</p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">8. Löschung und Aufbewahrung</h2>
            <p className="mb-6">
              Wir löschen Ihre Daten, sobald sie für den Zweck der Verarbeitung nicht mehr erforderlich sind und keine gesetzlichen Aufbewahrungspflichten (z. B. Handels- oder Steuerrecht) entgegenstehen.
            </p>

            <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4">9. Änderungen dieser Erklärung</h2>
            <p className="mb-6">
              Wir passen diese Datenschutzerklärung ggf. an aktuelle rechtliche oder technische Anforderungen an. Bitte prüfen Sie die Erklärung regelmäßig auf Aktualität.
            </p>

            <hr className="my-8 border-gray-200" />
            
            <p className="text-sm text-gray-500 italic">Stand: 15. Mai 2025</p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PrivacyPolicyPage;